import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSprings, useSpring, animated } from "@react-spring/web";

const IdealBusinessSection = () => {
  const items = [
    "Больше свободного времени для важных дел.",
    "Уверенность в себе и своих решениях.",
    "Бизнес, который приносит стабильность, прибыль и вдохновение.",
  ];

  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { threshold: 0.5 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  const titleAnimation = useSpring({
    from: { opacity: 0, transform: "translateY(-20px)" },
    to: inView ? { opacity: 1, transform: "translateY(0px)" } : { opacity: 0 },
    config: { tension: 80, friction: 20 },
    delay: 100,
  });

  const subtitleAnimation = useSpring({
    from: { opacity: 0 },
    to: inView ? { opacity: 1 } : { opacity: 0 },
    config: { tension: 80, friction: 20 },
    delay: 500,
  });

  const springs = useSprings(
    items.length,
    items.map((_, index) => ({
      from: { opacity: 0, transform: "translateX(-30px)" },
      to: inView
        ? { opacity: 1, transform: "translateX(0px)" }
        : { opacity: 0, transform: "translateX(-30px)" },
      delay: 2000 + index * 800,
      config: { tension: 80, friction: 20 },
    }))
  );

  return (
    <Box
      ref={sectionRef}
      sx={{
        background: "#000",
        color: "#fff",
        py: { xs: 6, md: 8 },
        px: { xs: 3, md: 6 },
        textAlign: { xs: "center", md: "center" },
        minHeight: { xs: "auto", md: "80vh" },
        display: "flex",
        flexDirection: "column",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "center",
      }}
    >
      <animated.div style={titleAnimation}>
        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            mb: 3,
            lineHeight: 1.3,
            fontSize: {
              xs: "2.1rem",
              md: "3.3rem",
            },
          }}
        >
          Представьте свой идеальный бизнес:
        </Typography>
      </animated.div>
      <animated.div style={subtitleAnimation}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 400,
            fontSize: {
              xs: "1.25rem",
              md: "1.5rem",
            },
          }}
        >
          Что если ваша текущая боль, страхи или ограничения исчезли?
        </Typography>
      </animated.div>
      <Grid
        container
        spacing={4}
        justifyContent="center"
        sx={{
          mt: 4,
          alignItems: { xs: "flex-start", md: "center" },
        }}
      >
        <Grid item xs={12} md={6}>
          <List>
            {springs.map((style, index) => (
              <animated.div key={index} style={style}>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "#16ef64" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={items[index]}
                    primaryTypographyProps={{
                      sx: {
                        textAlign: { xs: "left", md: "center" },
                      },
                    }}
                  />
                </ListItem>
              </animated.div>
            ))}
          </List>
        </Grid>
      </Grid>
      <Box
        sx={{
          maxWidth: "900px",
          mx: "auto",
          mt: 6,
          textAlign: { xs: "center", md: "center" },
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontStyle: "italic",
            color: "#fff",
            mb: 3,
          }}
        >
          Мой авторский подход{" "}
          <Typography
            variant="span"
            sx={{
              fontWeight: "bold",
              fontSize: {
                xs: "1rem",
                md: "1.25rem",
              },
            }}
          >
            “Действуй сейчас”{" "}
          </Typography>
          поможет вам достичь этого и даже большего. Ваша мечта может стать
          реальностью — начните уже сегодня!
        </Typography>
      </Box>
    </Box>
  );
};

export default IdealBusinessSection;
